// ============================================
// 4. Settings.js - Complete System Configuration
// ============================================

import React, { useState } from 'react';

const Settings = () => {
  const [apiStatuses, setApiStatuses] = useState({
    congress: { name: 'Congress.gov', status: 'Online', key: 'IVL...E9', lastUpdate: '2025-08-03 09:15:00', calls: 847, limit: 1000 },
    fred: { name: 'FRED Economic', status: 'Online', key: '15a...409', lastUpdate: '2025-08-03 09:14:30', calls: 234, limit: 1000 },
    alphaVantage: { name: 'Alpha Vantage', status: 'Online', key: 'WSS...QOV', lastUpdate: '2025-08-03 09:15:15', calls: 156, limit: 500 },
    finnhub: { name: 'Finnhub', status: 'Online', key: 'd1u...e00', lastUpdate: '2025-08-03 09:15:05', calls: 298, limit: 1000 },
    tiingo: { name: 'Tiingo', status: 'Online', key: '515...6f2', lastUpdate: '2025-08-03 09:14:50', calls: 89, limit: 1000 },
    polygon: { name: 'Polygon.io', status: 'Online', key: 'QYj...wwX', lastUpdate: '2025-08-03 09:15:10', calls: 445, limit: 1000 },
    quiver: { name: 'Quiver Quantitative', status: 'Online', key: 'Pro Tier', lastUpdate: '2025-08-03 09:14:45', calls: 178, limit: 2000 },
    openai: { name: 'OpenAI (Bob)', status: 'Online', key: 'sk-...Pro', lastUpdate: '2025-08-03 09:15:20', calls: 34, limit: 5000 }
  });

  const [riskSettings, setRiskSettings] = useState({
    maxPositionRisk: 2.5,
    maxPortfolioDrawdown: 10,
    mlConfidenceThreshold: 85,
    smartScoreThreshold: 7.0,
    maxPositions: 10,
    dailyLossLimit: 3,
    maxPortfolioHeat: 12
  });

  const [tradingSettings, setTradingSettings] = useState({
    autoExecution: false,
    paperTrading: true,
    alertsEnabled: true,
    governmentIntelEnabled: true,
    phoenixScalpEnabled: true,
    optionsCoreEnabled: true,
    globalMarketsEnabled: false
  });

  const getStatusColor = (status) => {
    switch(status) {
      case 'Online': return '#00FF5F';
      case 'Warning': return '#FFB800';
      case 'Offline': return '#FF0000';
      default: return '#a0a9c1';
    }
  };

  const getUsageColor = (used, limit) => {
    const percentage = (used / limit) * 100;
    if (percentage > 80) return '#FF0000';
    if (percentage > 60) return '#FFB800';
    return '#00FF5F';
  };

  const handleApiTest = (apiKey) => {
    alert(`Testing ${apiStatuses[apiKey].name} connection...`);
    // This would test the actual API connection
  };

  const handleSaveSettings = () => {
    alert('Settings saved successfully!');
    // This would save to backend/config
  };

  return (
    <div style={{
      position: 'relative',
      minHeight: '100vh',
      backgroundColor: '#1a2332',
      color: '#ffffff',
      fontFamily: 'Georgia, serif',
      padding: '24px'
    }}>
      {/* WATERMARK */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        opacity: '0.03',
        pointerEvents: 'none',
        zIndex: '1',
        width: '800px',
        height: '800px'
      }}>
        <img 
          src="/finance-watermark.jpg"
          alt="Financial Trading Watermark"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </div>

      <div style={{ position: 'relative', zIndex: '2', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '32px'
        }}>
          <div>
            <h1 style={{
              color: '#ffffff',
              fontSize: '32px',
              fontWeight: 'bold',
              margin: '0',
              fontFamily: 'Georgia, serif'
            }}>🔧 System Settings</h1>
            <p style={{
              color: '#a0a9c1',
              fontSize: '16px',
              margin: '8px 0 0 0',
              fontFamily: 'Georgia, serif'
            }}>API monitoring, risk parameters, and system configuration</p>
          </div>
          <button
            onClick={handleSaveSettings}
            style={{
              backgroundColor: '#00FF5F',
              color: '#202A44',
              border: 'none',
              borderRadius: '4px',
              padding: '12px 24px',
              cursor: 'pointer',
              fontFamily: 'Georgia, serif',
              fontWeight: 'bold'
            }}
          >
            💾 Save Settings
          </button>
        </div>

        {/* API Status Section */}
        <div style={{
          backgroundColor: '#202A44',
          padding: '24px',
          borderRadius: '8px',
          border: '1px solid #3a4a6b',
          marginBottom: '32px'
        }}>
          <h3 style={{ color: '#D4AF37', margin: '0 0 20px 0', fontFamily: 'Georgia, serif' }}>
            🔌 API Status & Monitoring
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '16px'
          }}>
            {Object.entries(apiStatuses).map(([key, api]) => (
              <div key={key} style={{
                backgroundColor: '#1a2332',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #3a4a6b'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: getStatusColor(api.status)
                    }}></div>
                    <h4 style={{ color: '#ffffff', margin: '0', fontFamily: 'Georgia, serif' }}>
                      {api.name}
                    </h4>
                  </div>
                  <button
                    onClick={() => handleApiTest(key)}
                    style={{
                      backgroundColor: '#0069B1',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontFamily: 'Georgia, serif'
                    }}
                  >
                    Test
                  </button>
                </div>
                <div style={{ color: '#a0a9c1', fontSize: '12px', fontFamily: 'Georgia, serif', lineHeight: '1.4' }}>
                  <div>Status: <span style={{ color: getStatusColor(api.status) }}>{api.status}</span></div>
                  <div>Key: {api.key}</div>
                  <div>Last Update: {api.lastUpdate}</div>
                  <div>
                    Usage: <span style={{ color: getUsageColor(api.calls, api.limit) }}>
                      {api.calls}/{api.limit} calls
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Risk Management Settings */}
        <div style={{
          backgroundColor: '#202A44',
          padding: '24px',
          borderRadius: '8px',
          border: '1px solid #3a4a6b',
          marginBottom: '32px'
        }}>
          <h3 style={{ color: '#D4AF37', margin: '0 0 20px 0', fontFamily: 'Georgia, serif' }}>
            🛡️ Risk Management Parameters
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px'
          }}>
            <div>
              <label style={{ color: '#a0a9c1', fontSize: '14px', display: 'block', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
                Max Position Risk (%)
              </label>
              <input
                type="number"
                step="0.1"
                value={riskSettings.maxPositionRisk}
                onChange={(e) => setRiskSettings({...riskSettings, maxPositionRisk: parseFloat(e.target.value)})}
                style={{
                  backgroundColor: '#1a2332',
                  color: '#ffffff',
                  border: '1px solid #3a4a6b',
                  borderRadius: '4px',
                  padding: '8px',
                  width: '100%',
                  fontFamily: 'Georgia, serif'
                }}
              />
            </div>
            <div>
              <label style={{ color: '#a0a9c1', fontSize: '14px', display: 'block', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
                Max Portfolio Drawdown (%)
              </label>
              <input
                type="number"
                value={riskSettings.maxPortfolioDrawdown}
                onChange={(e) => setRiskSettings({...riskSettings, maxPortfolioDrawdown: parseInt(e.target.value)})}
                style={{
                  backgroundColor: '#1a2332',
                  color: '#ffffff',
                  border: '1px solid #3a4a6b',
                  borderRadius: '4px',
                  padding: '8px',
                  width: '100%',
                  fontFamily: 'Georgia, serif'
                }}
              />
            </div>
            <div>
              <label style={{ color: '#a0a9c1', fontSize: '14px', display: 'block', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
                ML Confidence Threshold (%)
              </label>
              <input
                type="number"
                value={riskSettings.mlConfidenceThreshold}
                onChange={(e) => setRiskSettings({...riskSettings, mlConfidenceThreshold: parseInt(e.target.value)})}
                style={{
                  backgroundColor: '#1a2332',
                  color: '#ffffff',
                  border: '1px solid #3a4a6b',
                  borderRadius: '4px',
                  padding: '8px',
                  width: '100%',
                  fontFamily: 'Georgia, serif'
                }}
              />
            </div>
            <div>
              <label style={{ color: '#a0a9c1', fontSize: '14px', display: 'block', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
                Smart Score Threshold
              </label>
              <input
                type="number"
                step="0.1"
                value={riskSettings.smartScoreThreshold}
                onChange={(e) => setRiskSettings({...riskSettings, smartScoreThreshold: parseFloat(e.target.value)})}
                style={{
                  backgroundColor: '#1a2332',
                  color: '#ffffff',
                  border: '1px solid #3a4a6b',
                  borderRadius: '4px',
                  padding: '8px',
                  width: '100%',
                  fontFamily: 'Georgia, serif'
                }}
              />
            </div>
            <div>
              <label style={{ color: '#a0a9c1', fontSize: '14px', display: 'block', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
                Max Concurrent Positions
              </label>
              <input
                type="number"
                value={riskSettings.maxPositions}
                onChange={(e) => setRiskSettings({...riskSettings, maxPositions: parseInt(e.target.value)})}
                style={{
                  backgroundColor: '#1a2332',
                  color: '#ffffff',
                  border: '1px solid #3a4a6b',
                  borderRadius: '4px',
                  padding: '8px',
                  width: '100%',
                  fontFamily: 'Georgia, serif'
                }}
              />
            </div>
            <div>
              <label style={{ color: '#a0a9c1', fontSize: '14px', display: 'block', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
                Daily Loss Limit (%)
              </label>
              <input
                type="number"
                value={riskSettings.dailyLossLimit}
                onChange={(e) => setRiskSettings({...riskSettings, dailyLossLimit: parseInt(e.target.value)})}
                style={{
                  backgroundColor: '#1a2332',
                  color: '#ffffff',
                  border: '1px solid #3a4a6b',
                  borderRadius: '4px',
                  padding: '8px',
                  width: '100%',
                  fontFamily: 'Georgia, serif'
                }}
              />
            </div>
          </div>
        </div>

        {/* Trading Settings */}
        <div style={{
          backgroundColor: '#202A44',
          padding: '24px',
          borderRadius: '8px',
          border: '1px solid #3a4a6b'
        }}>
          <h3 style={{ color: '#D4AF37', margin: '0 0 20px 0', fontFamily: 'Georgia, serif' }}>
            📊 Trading Configuration
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px'
          }}>
            {Object.entries(tradingSettings).map(([key, value]) => (
              <div key={key} style={{
                backgroundColor: '#1a2332',
                padding: '16px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <input
                  type="checkbox"
                  checked={value}
                  onChange={(e) => setTradingSettings({...tradingSettings, [key]: e.target.checked})}
                  style={{ width: '16px', height: '16px' }}
                />
                <label style={{ color: '#ffffff', fontSize: '14px', fontFamily: 'Georgia, serif' }}>
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;